import React from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import { DataAmount, Icon, Tooltip } from '@toqio/toqio-web-components';
import { CARD_SHADOW, MOBILE, tok } from './styles';

/**
 * The "Available Balance" card (PRD §5). Reproduces the source `cloud-sub-panel`
 * `.border-custom` box (MAPPINGS → Card container / sub-panel) as a flow-local
 * styled-component reading theme tokens.
 *
 * The amount uses the DS `DataAmount` (MAPPINGS → Data amount): its first row is
 * `label` + an icon, the second is `value` + `currency`. The info-icon tooltip
 * goes through `DataAmount`'s own `customIcon` slot (not a hand-rolled label
 * row), wrapping a DS `Icon` in a DS `Tooltip`.
 *
 * The tooltip shows the raw `accountId` (PRD §4 / O-4). The new GET does not
 * return an account id yet, so the icon renders only when `accountId` is
 * present (graceful degrade until the BE ships the field).
 */
const Card = styled.section`
  display: flex;
  flex-direction: column;
  padding: 24px;
  border: 1px solid ${({ theme }) => tok(theme, 'containers_bordered_border', '#DADADA')};
  background: ${({ theme }) => tok(theme, 'containers_bordered_background', '#FFFFFF')};
  border-radius: 12px;
  box-shadow: ${CARD_SHADOW};
  min-width: 280px;
  max-width: 420px;
  width: 100%;

  @media (max-width: ${MOBILE}) {
    max-width: none;
  }

  /* Source label is bold + primary; the DS default is a lighter secondary. */
  [data-testid='data-amount-label'] {
    font-weight: 700;
    color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
  }
`;

const InfoIcon = styled.span`
  display: inline-flex;
  cursor: help;
  color: ${({ theme }) => tok(theme, 'general_font_secondary', '#667085')};
`;

export type BalanceCardProps = {
  formattedBalance: string;
  currency: string;
  accountId?: string;
};

export const BalanceCard: React.FC<BalanceCardProps> = ({
  formattedBalance,
  currency,
  accountId,
}) => (
  <Card>
    <DataAmount
      label={t`Available Balance`}
      value={formattedBalance}
      currency={currency}
      size="m"
      weight="bold"
      customIcon={
        accountId ? (
          // Use the `title` prop, not `content`: the DS only applies the
          // themed tooltip font (white) to `title`; raw `content` inherits the
          // surrounding text colour (renders black).
          <Tooltip title={accountId} position="top">
            <InfoIcon>
              <Icon name="icn_info" size="16px" />
            </InfoIcon>
          </Tooltip>
        ) : undefined
      }
    />
  </Card>
);
